import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import localforage from "localforage";
import { JobVisitType, PartnerJobVisitsStorageKey } from "models/JobVisitType";
import { createUpdateVisitVars } from "operations/mutations/UpdateVisit";
import {
  JobStatus,
  MeterReadingInputType,
  PropertyValueInputType,
  TaskStatus,
  UpdateMachineInputType,
  UploadFileMutationVariables,
} from "operations/schema/schema";
import { AppAsyncThunkConfig } from "store";
import { State, storePartnerVisitsToLocal } from "./partner.store";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

export const asyncMutations = {
  loadJobVisitsInit: createAppAsyncThunk(
    "partner/loadJobVisitsInit",
    async (_, { rejectWithValue }) => {
      try {
        const value = await localforage.getItem(PartnerJobVisitsStorageKey);
        return { jobVisits: value };
      } catch (err) {
        return rejectWithValue("unable to load in stored job visits");
      }
    }
  ),
  acceptJob: createAppAsyncThunk(
    "partner/acceptJob",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { token } = getState().partner;
      if (!token) return rejectWithValue("No selected job");
      const { errors, queued } = await graphqlRequest(sdk.updateJobStatus, {
        thunkName: "partner/acceptJob",
        thunkProps: { ...props },
        variables: {
          jobId,
          status: JobStatus.Accepted,
        },
      });
      if (errors) return rejectWithValue(errors);
      return { queued };
    }
  ),
  uploadFile: createAppAsyncThunk(
    "partner/uploadFile",
    async (props: UploadFileMutationVariables, { rejectWithValue, extra: { sdk } }) => {
      const { jobId, file } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.uploadFile, {
        thunkName: "partner/uploadFile",
        thunkProps: props,
        variables: {
          jobId,
          file: {
            name: file.name,
            data: file.data,
            description: file.description,
          },
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.uploadFile) return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),

  //EditMachineLocationDialog
  updateMachineLocation: createAppAsyncThunk(
    "partner/updateMachineLocation",
    async (
      props: { jobId: string; machine: UpdateMachineInputType },
      { rejectWithValue, extra: { sdk } }
    ) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateMachineLocation, {
        thunkName: "partner/updateMachineLocation",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateMachineLocation) return rejectWithValue("something went wrong");
      return { queued };
    }
  ),
  //EditMachineInstallDateDialog
  updateMachineInstallDate: createAppAsyncThunk(
    "partner/updateMachineInstallDate",
    async (
      props: { jobId: string; machine: UpdateMachineInputType },
      { rejectWithValue, extra: { sdk } }
    ) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateMachineInstallDate, {
        thunkName: "partner/updateMachineInstallDate",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateMachineInstallDate) return rejectWithValue("something went wrong");
      return { queued };
    }
  ),
  //ChangeJobMachineDialog
  changeJobMachine: createAppAsyncThunk(
    "partner/changeJobMachine",
    async (props: { jobId: string; machineId: string }, { rejectWithValue, extra: { sdk } }) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.changeJobMachine, {
        thunkName: "partner/changeJobMachine",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.changeJobMachine) return rejectWithValue("something went wrong");
      return { queued };
    }
  ),

  // Visit Specific
  completeVisit: createAppAsyncThunk(
    "partner/completeVisit",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const {
        user: { userVar },
        partner: { token, jobVisits },
      } = getState();
      if (!userVar) return rejectWithValue("No user");
      if (!jobVisits[jobId] || !token) return rejectWithValue("No job");
      const completeVisitVars = createUpdateVisitVars(jobId, jobVisits[jobId], TaskStatus.Done);

      const { data, errors, queued } = await graphqlRequest(sdk.updateVisitWithParts, {
        thunkName: "partner/completeVisit",
        thunkProps: props,
        variables: completeVisitVars,
        onError: [{ type: "partner/resetJobStatus", payload: { jobId } }],
      });

      if (errors) return rejectWithValue(errors);
      if (data && !data.updateVisitWithParts) return rejectWithValue("something went wrong");
      return { data, queued, followUp: jobVisits[jobId].followUp.followUpChecked };
    }
  ),
  updateVisit: createAppAsyncThunk(
    "partner/updateVisit",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const {
        partner: { jobVisits, token },
        user: { userVar },
      } = getState();
      if (!userVar) return rejectWithValue("No user");
      if (!jobVisits[jobId] || !token) return rejectWithValue("No job");
      const updateVisitVars = createUpdateVisitVars(jobId, jobVisits[jobId], TaskStatus.Accepted);

      const { data, errors, queued } = await graphqlRequest(sdk.updateVisitWithParts, {
        thunkName: "partner/updateVisit",
        thunkProps: props,
        variables: updateVisitVars,
      });

      if (errors) return rejectWithValue(errors);
      if (data && !data.updateVisitWithParts) return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),

  updateMachineCustomProps: createAppAsyncThunk(
    "partner/updateMachineCustomProps",
    async (
      props: { jobId: string; properties: PropertyValueInputType[] },
      { getState, rejectWithValue, extra: { sdk } }
    ) => {
      const { jobId, properties } = props;
      const { token } = getState().partner;
      if (!token) return rejectWithValue("No job");
      const { data, errors, queued } = await graphqlRequest(sdk.updateMachineCustomProperties, {
        thunkName: "partner/updateMachineCustomProps",
        thunkProps: props,
        variables: {
          jobId,
          properties,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateMachineCustomProperties)
        return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),
  updateMeters: createAppAsyncThunk(
    "partner/updateMeters",
    async (
      props: { jobId: string; meters: MeterReadingInputType | MeterReadingInputType[] },
      { getState, rejectWithValue, extra: { sdk } }
    ) => {
      const { jobId, meters } = props;
      const { token } = getState().partner;
      if (!token) return rejectWithValue("No job");
      const { data, errors, queued } = await graphqlRequest(sdk.updateMeters, {
        thunkName: "partner/updateMeters",
        thunkProps: props,
        variables: {
          jobId,
          meters,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateMeters) return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),
};

export const mutationBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(asyncMutations.acceptJob.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.jobs[meta.arg.jobId].status = JobStatus.Downloaded;
    return state;
  });
  builder.addCase(asyncMutations.acceptJob.fulfilled, (state, { meta }) => {
    state.jobs[meta.arg.jobId].status = JobStatus.Accepted;
    return state;
  });
  // Upload File
  builder.addCase(asyncMutations.uploadFile.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.fileUploading = true;
    return state;
  });
  builder.addCase(asyncMutations.uploadFile.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.fileUploading = false;
  });
  builder.addCase(asyncMutations.uploadFile.fulfilled, (state) => {
    state.fileUploading = false;
    return state;
  });
  // EditMachineLocationDialog
  builder.addCase(asyncMutations.updateMachineLocation.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.editMachineLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineLocation.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.editMachineLoading = false;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineLocation.fulfilled, (state, { meta }) => {
    state.editMachineLoading = false;
    const { jobId, machine } = meta.arg;
    if (state.jobs[jobId].machine) {
      state.jobs[jobId].machine!.location = machine.location;
    }
    return state;
  });
  // EditMachineInstallDateDialog
  builder.addCase(asyncMutations.updateMachineInstallDate.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.editMachineLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineInstallDate.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.editMachineLoading = false;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineInstallDate.fulfilled, (state, { meta }) => {
    state.editMachineLoading = false;
    const { jobId, machine } = meta.arg;
    if (state.jobs[jobId].machine) {
      state.jobs[jobId].machine!.installDate = machine.installDate;
    }
    return state;
  });
  // ChangeJobMachineDialog
  builder.addCase(asyncMutations.changeJobMachine.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.changeJobMachine.loading = true;
    return state;
  });
  builder.addCase(asyncMutations.changeJobMachine.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.changeJobMachine.loading = false;
    return state;
  });
  builder.addCase(asyncMutations.changeJobMachine.fulfilled, (state) => {
    state.changeJobMachine.loading = false;
    return state;
  });

  // Visit Specific
  function processUpdateVisitState(jobVisit: JobVisitType, infoString?: string) {
    // Handling for needing to dig deeper into completion status
    //const infos = infoString.split(",");
    //for (let info of infos) {
    //  if (info.includes("UploadFiles")) {
    //  }
    //}

    // Info string contains completed tasks
    if (infoString) {
      if (infoString.includes("UploadFiles")) {
        jobVisit.files = [];
      }
      const visitUploaded = infoString.includes("CompleteVisit");
      if (visitUploaded) {
        let extras = jobVisit.extras.map((e) => ({
          ...e,
          uploaded: true,
          pendingUpload: true,
        }));
        jobVisit.extras = [...extras];

        let workTimes = jobVisit.workTimes.map((t) => ({
          ...t,
          uploaded: t.startTime && t.stopTime ? true : false,
          pendingUpload: t.startTime && t.stopTime ? true : false,
        }));
        jobVisit.workTimes = [...workTimes];

        let travelTimes = jobVisit.travelTimes.map((t) => ({
          ...t,
          uploaded: t.startTime && t.stopTime ? true : false,
          pendingUpload: t.startTime && t.stopTime ? true : false,
        }));
        jobVisit.travelTimes = [...travelTimes];

        let usedParts = jobVisit.usedParts.map((p) => ({
          ...p,
          uploaded: true,
          pendingUpload: true,
        }));
        jobVisit.usedParts = [...usedParts];
      } else {
        let extras = jobVisit.extras.map((e) => ({
          ...e,
          pendingUpload: e.uploaded,
        }));
        jobVisit.extras = [...extras];

        let workTimes = jobVisit.workTimes.map((e) => ({
          ...e,
          pendingUpload: e.uploaded,
        }));
        jobVisit.workTimes = [...workTimes];

        let travelTimes = jobVisit.travelTimes.map((e) => ({
          ...e,
          pendingUpload: e.uploaded,
        }));
        jobVisit.travelTimes = [...travelTimes];

        let usedParts = jobVisit.usedParts.map((p) => ({
          ...p,
          pendingUpload: p.uploaded,
        }));
        jobVisit.usedParts = [...usedParts];
      }
    }
    return jobVisit;
  }

  builder.addCase(asyncMutations.completeVisit.rejected, (state, { payload: errors, meta }) => {
    if (!errors) throw new Error("Something went horribly wrong; error undefined");
    if (typeof errors === "string") throw new Error(errors);

    const id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;

    const { infoString } = ((messages: string[]): { shortMessage: string; infoString?: string } => {
      for (let message of messages) {
        if (!message.includes("|")) continue;
        const [s0] = message.split(" at ");
        const [shortMessage, infoString] = s0.split("|");
        return { shortMessage, infoString };
      }
      return {
        shortMessage: messages[0],
      };
    })(errors.map((e) => e.message));
    state.jobVisits[id] = processUpdateVisitState(state.jobVisits[id], infoString);
    storePartnerVisitsToLocal(state);
    return state;
  });
  builder.addCase(asyncMutations.completeVisit.pending, (state, { meta }) => {
    if (meta.queued) return state;
    let id = state.selectedJobId;
    if (!id) throw Error("Missing selectedId");
    state.jobVisits[id].submitLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.completeVisit.fulfilled, (state, { meta, payload: { data } }) => {
    let id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;
    state.jobVisits[id] = processUpdateVisitState(
      state.jobVisits[id],
      data?.updateVisitWithParts?.responseLog
    );
    state.token = null;
    return state;
  });

  builder.addCase(asyncMutations.updateVisit.rejected, (state, { payload: errors, meta }) => {
    if (!errors) throw new Error("Something went horribly wrong; error undefined");
    if (typeof errors === "string") throw new Error(errors);

    const id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;

    const { infoString } = ((messages: string[]): { shortMessage: string; infoString?: string } => {
      for (let message of messages) {
        if (!message.includes("|")) continue;
        const [s0] = message.split(" at ");
        const [shortMessage, infoString] = s0.split("|");
        return { shortMessage, infoString };
      }
      return {
        shortMessage: messages[0],
      };
    })(errors.map((e) => e.message));

    state.jobVisits[id] = processUpdateVisitState(state.jobVisits[id], infoString);
    storePartnerVisitsToLocal(state);
    return state;
  });
  builder.addCase(asyncMutations.updateVisit.pending, (state, { meta }) => {
    if (meta.queued) return state;
    let id = state.selectedJobId;
    if (!id) throw Error("Missing selectedId");
    state.jobVisits[id].submitLoading = true;
    let extras = state.jobVisits[id].extras.map((e) => ({
      ...e,
      pendingUpload: true,
    }));
    state.jobVisits[id].extras = [...extras];
    let parts = state.jobVisits[id].usedParts.map((p) => ({
      ...p,
      pendingUpload: true,
    }));
    state.jobVisits[id].usedParts = [...parts];
    let workTimes = state.jobVisits[id].workTimes.map((t) => ({
      ...t,
      pendingUpload: t.startTime && t.stopTime ? true : false,
    }));
    state.jobVisits[id].workTimes = [...workTimes];
    let travelTimes = state.jobVisits[id].travelTimes.map((t) => ({
      ...t,
      pendingUpload: t.startTime && t.stopTime ? true : false,
    }));
    state.jobVisits[id].travelTimes = [...travelTimes];
    return state;
  });
  builder.addCase(asyncMutations.updateVisit.fulfilled, (state, { meta, payload: { data } }) => {
    let id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;
    state.jobVisits[id] = processUpdateVisitState(
      state.jobVisits[id],
      data?.updateVisitWithParts?.responseLog
    );
    storePartnerVisitsToLocal(state);
    return state;
  });

  builder.addCase(asyncMutations.updateMachineCustomProps.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.editMachineLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineCustomProps.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.editMachineLoading = false;
  });
  builder.addCase(asyncMutations.updateMachineCustomProps.fulfilled, (state) => {
    state.editMachineLoading = false;
    return state;
  });
};
